import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PremiumLayout from "./PremiumLayout";
import { http } from "../../lib/api";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// PremiumBuilders — editorial layout with the premium dark/gold palette.
// Hero images, the 4 stat tiles and the testimonial are all CMS-editable via
// /marketing-admin → Builders Corner. Hard-coded fallbacks below are only used
// on a fresh install where the studio hasn't populated the CMS yet.
// -----------------------------------------------------------------------------

const DEFAULT_HERO_LEFT  = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1400&auto=format&fit=crop&q=80";
const DEFAULT_HERO_RIGHT = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1400&auto=format&fit=crop&q=80";

const OFFERINGS = [
  { n: "01", t: "Site inspections & structural audits",
    d: "RCC quality reviews, formwork checks, rebar cover verification, mix-design validation and sequencing critique — delivered as a signed report you can act on." },
  { n: "02", t: "Design partner for developers",
    d: "Long-term in-house design role for builders — layouts, façades, interiors and sales-ready renders, drawn in the vocabulary of your target buyer." },
  { n: "03", t: "Turnkey execution",
    d: "Architecture + structure + MEP + interiors — one contract, one accountability, one team on your site from ground-breaking to handover." },
  { n: "04", t: "Value engineering",
    d: "Reworking existing drawings for cost-per-sqft efficiency without touching structural integrity or the spatial promise you sold to buyers." },
  { n: "05", t: "Sales gallery & show flat",
    d: "Fully designed & executed sample flats that let your on-site sales team close in the space itself — furniture, lighting, styling, wayfinding included." },
  { n: "06", t: "Landscape & clubhouse",
    d: "The last 10% of a project that decides how your buyers feel walking in — courtyards, pool decks, lobbies, and the small architectural moves that photograph." },
];

const PROCESS = [
  { n: "I",   t: "Scope",       d: "You send us a plot / drawing / photo. We reply in 24 hours with fit or no-fit." },
  { n: "II",  t: "Site visit",  d: "One of our principals visits your site. No junior handoffs, no proposals-by-email." },
  { n: "III", t: "Estimate",    d: "Fixed-scope PDF with material specs, sequencing, and per-sqft economics." },
  { n: "IV",  t: "Handover",    d: "Weekly walk-throughs, monthly finance reviews, then a photograph-ready handover." },
];

export default function PremiumBuilders() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", scope: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cms, setCms] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    http.get("/marketing/builders")
      .then(({ data }) => setCms(data))
      .catch(() => setCms({}));
  }, []);

  const heroLeft   = cms?.hero_left_url  || DEFAULT_HERO_LEFT;
  const heroRight  = cms?.hero_right_url || DEFAULT_HERO_RIGHT;
  const stats      = (cms?.stats && cms.stats.length === 4) ? cms.stats : [
    { k: "10+", l: "Years partnering with builders" },
    { k: "60+", l: "Live construction sites reviewed" },
    { k: "18",  l: "In-house engineers & architects" },
    { k: "12",  l: "Cities served across India" },
  ];
  const quote = cms?.testimonial || {
    body: "They walked our site twice before drawing a single line. That's rare — and it's the reason our tower is on schedule.",
    who: "R. Kulkarni",
    role: "Managing Partner · Kulkarni Constructions, Pune",
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.scope.trim()) {
      toast.error("Name and a short scope are required");
      return;
    }
    setBusy(true);
    try {
      await http.post("/marketing/builders/callback", {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        scope: form.scope.trim(),
      });
      setSent(true);
      toast.success("Thanks — one of our senior architects will call you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <PremiumLayout>
      {/* =========================== HERO — split ============================ */}
      <section className="relative overflow-hidden" data-testid="builders-hero">
        <div className="grid md:grid-cols-2 min-h-[64vh]">
          <div className="relative overflow-hidden">
            <img src={heroLeft} alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
            <div className="absolute inset-0 bg-[#0A0A0A]/45" />
          </div>
          <div className="relative overflow-hidden hidden md:block">
            <img src={heroRight} alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
            <div className="absolute inset-0 bg-[#0A0A0A]/55" />
          </div>
        </div>

        {/* Editorial overlay title — sits on top of both halves */}
        <div className="absolute inset-0 flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="max-w-2xl">
              <div className="gold text-[11px] tracking-[.22em] uppercase fade-up">Builder&apos;s Corner</div>
              <h1 className="serif text-5xl md:text-7xl leading-[0.98] mt-5 fade-up cream">
                A studio that walks <span className="gold italic">your site</span>
                <span className="block mt-2">before it draws a line.</span>
              </h1>
              <p className="text-[#DDD5C4] mt-8 max-w-lg leading-relaxed fade-up-slow">
                For builders, developers and plot owners — we work as your extended in-house
                architecture &amp; engineering studio, not a vendor.
              </p>
              <div className="mt-10 flex gap-3 fade-up-slow">
                <a href="#builders-callback"
                   className="mono text-[11px] tracking-[.22em] uppercase border border-[#C9A96E] gold px-7 py-4 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors">
                  Request a callback →
                </a>
                <Link to="/projects"
                      className="mono text-[11px] tracking-[.22em] uppercase border border-[#2A2A2A] text-[#DDD5C4] px-7 py-4 hover:border-[#C9A96E] transition-colors">
                  See recent sites
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================== STATS RAIL ============================ */}
      <section className="border-y border-[#1F1F1F]" data-testid="builders-stats">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
          {stats.map((s, i) => (
            <div key={`${s.l}-${i}`}
                 className={`py-10 px-4 md:px-8 ${i > 0 ? "md:border-l" : ""} ${i > 1 || (i > 0) ? "border-t md:border-t-0" : ""} border-[#1F1F1F]`}>
              <div className="serif text-4xl md:text-5xl gold">{s.k}</div>
              <div className="text-[11px] tracking-[.22em] uppercase text-[#9A9284] mt-3">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* =========================== EDITORIAL LEDE ============================ */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-28" data-testid="builders-lede">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-16">
          <div>
            <div className="gold text-[11px] tracking-[.22em] uppercase sticky top-32">Introduction</div>
          </div>
          <div>
            <p className="serif text-3xl md:text-[38px] leading-[1.28] cream">
              Whether you&apos;re a builder holding a plot, a developer scaling a residential
              tower, or a landowner wondering what your site could become — we work with you
              like a <span className="gold italic">senior partner</span>, not a supplier.
            </p>
            <p className="text-[#B8B0A0] mt-8 leading-relaxed text-lg max-w-2xl">
              Every project starts with a studio principal walking your site, sketching in
              ink, and telling you honestly whether we&apos;re a fit. If we&apos;re not, we&apos;ll say so
              — and often introduce you to the studio that is.
            </p>
          </div>
        </div>
      </section>

      {/* =========================== SERVICE GRID ============================ */}
      <section className="border-t border-[#1F1F1F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-16 mb-16">
            <div className="gold text-[11px] tracking-[.22em] uppercase">What we offer</div>
            <h2 className="serif text-4xl md:text-6xl leading-[1.02] cream">
              A short menu.<br /><span className="gold italic">Done properly.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#1F1F1F]">
            {OFFERINGS.map((o) => (
              <article key={o.n} data-testid={`builder-offering-${o.n}`}
                       className="bg-[#0A0A0A] p-10 hover:bg-[#111111] transition-colors group cursor-default">
                <div className="flex items-baseline justify-between">
                  <div className="serif text-4xl gold">{o.n}</div>
                  <div className="h-px w-10 bg-[#2A2A2A] group-hover:bg-[#C9A96E] transition-colors" />
                </div>
                <h3 className="serif text-2xl md:text-[26px] mt-6 leading-tight cream">{o.t}</h3>
                <p className="text-sm text-[#9A9284] mt-4 leading-relaxed">{o.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== PROCESS ============================ */}
      <section className="border-t border-[#1F1F1F] bg-[#0D0D0D]" data-testid="builders-process">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-16 mb-16">
            <div className="gold text-[11px] tracking-[.22em] uppercase">How we work</div>
            <h2 className="serif text-4xl md:text-6xl leading-[1.02] cream">
              Four steps.<br /><span className="gold italic">Zero surprises.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {PROCESS.map((p, i) => (
              <div key={p.n} className="relative pl-6" data-testid={`builder-step-${p.n}`}>
                <div className="absolute left-0 top-0 w-px h-full bg-[#2A2A2A]" />
                <div className="absolute -left-[3px] top-0 w-1.5 h-1.5 bg-[#C9A96E] rounded-full" />
                <div className="text-[11px] tracking-[.22em] uppercase text-[#9A9284] mb-3">Step {p.n}</div>
                <div className="serif text-3xl gold">{p.t}</div>
                <p className="text-sm text-[#B8B0A0] mt-4 leading-relaxed">{p.d}</p>
                <div className="mt-6 text-[10px] tracking-[.22em] uppercase text-[#5A544A]">
                  {i < PROCESS.length - 1 ? `→ leads to Step ${PROCESS[i + 1].n}` : "→ Ongoing"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== QUOTE ============================ */}
      <section className="border-t border-[#1F1F1F]">
        <blockquote className="max-w-[1000px] mx-auto px-6 md:px-10 py-28 text-center" data-testid="builders-quote">
          <div className="gold serif text-6xl leading-none">&ldquo;</div>
          <p className="serif text-3xl md:text-4xl leading-[1.3] cream mt-4 italic">{quote.body}</p>
          <div className="mt-8 text-[11px] tracking-[.22em] uppercase text-[#9A9284]">
            <span className="gold">{quote.who}</span> · {quote.role}
          </div>
        </blockquote>
      </section>

      {/* =========================== CALLBACK FORM ============================ */}
      <section id="builders-callback" className="border-t border-[#1F1F1F] bg-[#0D0D0D]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-[1fr_1.2fr] gap-16 items-start">
          <div>
            <div className="gold text-[11px] tracking-[.22em] uppercase">Talk to a senior architect</div>
            <h2 className="serif text-4xl md:text-5xl leading-[1.05] mt-4 cream">
              Send us a scope.<br /><span className="gold italic">We&apos;ll call within a day.</span>
            </h2>
            <p className="text-[#B8B0A0] mt-6 leading-relaxed">
              Every message routes straight to a studio principal — never a call centre, never
              a form-fill autoresponder. Share what you&apos;re building and we&apos;ll pick up the phone.
            </p>
            <div className="mt-10 space-y-4">
              <div className="flex items-baseline gap-4">
                <div className="text-[10px] tracking-[.22em] uppercase text-[#9A9284] w-16">Call</div>
                <a href="tel:+919000000000" className="gold text-lg gold-link">+91 90000 00000</a>
              </div>
              <div className="flex items-baseline gap-4">
                <div className="text-[10px] tracking-[.22em] uppercase text-[#9A9284] w-16">Email</div>
                <a href="mailto:hello@dreamhomeplanners.com" className="gold text-lg gold-link">hello@dreamhomeplanners.com</a>
              </div>
              <div className="flex items-baseline gap-4">
                <div className="text-[10px] tracking-[.22em] uppercase text-[#9A9284] w-16">Hours</div>
                <div className="text-[#DDD5C4]">Mon – Sat · 10:00 to 19:00 IST</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="builders-form"
                className="border border-[#1F1F1F] bg-[#0A0A0A] p-8 md:p-10 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <Field label="Your Name *">
                <input required data-testid="bc-name" value={form.name} onChange={set("name")}
                       className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
              </Field>
              <Field label="Company / Firm">
                <input data-testid="bc-company" value={form.company} onChange={set("company")}
                       className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
              </Field>
            </div>
            <Field label="Phone">
              <input data-testid="bc-phone" value={form.phone} onChange={set("phone")}
                     className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
            </Field>
            <Field label="Project Scope *">
              <textarea required data-testid="bc-scope" rows={5} value={form.scope} onChange={set("scope")}
                        placeholder="e.g. 8-storey residential tower, plot 240×90 ft, structural review + interior scope"
                        className="w-full bg-transparent border border-[#2A2A2A] focus:border-[#C9A96E] outline-none p-4 cream placeholder:text-[#5A544A]" />
            </Field>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <div className="text-[10px] tracking-[.22em] uppercase text-[#5A544A]">
                Your message reaches Studio Principals only
              </div>
              <button type="submit" disabled={busy || sent} data-testid="bc-submit"
                      className="mono text-[11px] tracking-[.22em] uppercase border border-[#C9A96E] gold px-8 py-4 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors disabled:opacity-50">
                {sent ? "Sent — thank you." : busy ? "Sending…" : "Request a Callback →"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* =========================== SITE-VISIT CTA ============================ */}
      <section className="border-t border-[#1F1F1F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <div>
            <div className="gold text-[11px] tracking-[.22em] uppercase">Prefer a walk-through?</div>
            <div className="serif text-3xl md:text-4xl mt-2 cream">Book a site visit.</div>
          </div>
          <Link to="/contact" data-testid="builders-site-visit"
                className="mono text-[11px] tracking-[.22em] uppercase border border-[#C9A96E] gold px-8 py-4 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors">
            Contact the studio →
          </Link>
        </div>
      </section>
    </PremiumLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[.22em] uppercase text-[#9A9284] mb-3">{label}</div>
      {children}
    </label>
  );
}
